import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";
import IlssQualiteCollePanel from "./IlssQualiteCollePanel";

import { useI18n } from "../i18n/I18nContext";

// ── Helpers ───────────────────────────────────────────────────────────────────
const toF = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  // Format Decimal DynamoDB : { "$n": "12.34" } ou { "value": "12.34" }
  if (typeof v === "object") {
    v = v["$n"] ?? v["value"] ?? v["S"] ?? Object.values(v)[0] ?? 0;
  }
  const n = parseFloat(String(v).replace(",", "."));
  return isNaN(n) ? 0 : n;
};

function parseDate(str) {
  if (!str) return null;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split("/");
    return new Date(`${y}-${m}-${d}`);
  }
  return new Date(str);
}
const q2  = (v) => Math.round(v * 100) / 100;

function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

const COLORS = {
  green:  "#4ade80", red:    "#f87171", yellow: "#E6FF3E",
  blue:   "#60a5fa", orange: "#fb923c", purple: "#c084fc",
  teal:   "#2dd4bf", gray:   "#64748b",
};

// Seuils
const SEUIL_ECART_EP   = 0.2;   // mm — écart max épaisseur
const SEUIL_TAUX_COMPR = 15;    // % — taux compression max
const SEUIL_DENSITE    = { min: 0.8, max: 1.5 }; // g/cm³

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ ...S.kpiCard, borderTopColor: color }}>
      <div style={S.kpiIcon}>{icon}</div>
      <div style={{ ...S.kpiValue, color }}>{value}</div>
      <div style={S.kpiLabel}>{label}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

// ── Gauge SVG ─────────────────────────────────────────────────────────────────
function Gauge({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const r = 52; const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={S.gaugeWrap}>
      <svg width="124" height="124" viewBox="0 0 124 124">
        <circle cx="62" cy="62" r={r} fill="none" stroke="#1e3a50" strokeWidth="11" />
        <circle cx="62" cy="62" r={r} fill="none" stroke={color}
          strokeWidth="11" strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round" transform="rotate(-90 62 62)"
          style={{ transition: "stroke-dasharray 0.8s ease" }} />
        <text x="62" y="57" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="800">{pct}%</text>
        <text x="62" y="75" textAnchor="middle" fill="#94a3b8" fontSize="10">{value}/{total}</text>
      </svg>
      <div style={S.gaugeLabel}>{label}</div>
    </div>
  );
}

// ── Tooltip custom ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={S.tooltip}>
      <div style={{ color: "#E6FF3E", fontWeight: 700, marginBottom: 6, fontSize: 12 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontSize: 12, marginBottom: 2 }}>
          {p.name} : <strong>{typeof p.value === "number" ? q2(p.value) : p.value}</strong>
        </div>
      ))}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function DashboardQualiteStack() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [items,   setItems]   = useState([]);
  const [ilssItems, setIlssItems] = useState([]);
  const [ilssLoading, setIlssLoading] = useState(true);
  const [days,    setDays]    = useState(30);
  const [search,  setSearch]  = useState("");

  // ── Chargement ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      setIlssLoading(true);
      try {
        const [stackRes, ilssRes] = await Promise.all([
          apiFetch("/api/qualite/stack/list"),
          apiFetch("/api/ilss/list"),
        ]);
        const data = await stackRes.json();
        const arr  = Array.isArray(data) ? data : data.items || [];
        setItems(arr);

        if (ilssRes.ok) {
          const ilssData = await ilssRes.json();
          setIlssItems(Array.isArray(ilssData) ? ilssData : ilssData.items || []);
        }
      } catch (e) {
        setError("Erreur chargement données stack.");
      } finally {
        setLoading(false);
        setIlssLoading(false);
      }
    })();
  }, []);

  // ── Filtre période + recherche ──────────────────────────────────────────────
const filtered = useMemo(() => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  cutoff.setHours(0, 0, 0, 0);

  return items.filter(r => {
    const d = parseDate(r.Date) || parseDate(r.Timestamp);
    if (d) d.setHours(0, 0, 0, 0);

    const inPeriod = d && d >= cutoff;
    const inSearch =
      !search.trim() ||
      (r.IDStack || "").toLowerCase().includes(search.trim().toLowerCase());

    return inPeriod && inSearch;
  });
}, [items, days, search]);

  // ── Calculs ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!filtered.length) return null;

    // KPIs
    const conf      = filtered.filter(r => r.Conformite === "CONFORME").length;
    const nonConf   = filtered.filter(r => r.Conformite === "NON CONFORME").length;
    const epMoys    = filtered.map(r => toF(r.Epaisseur_Moyenne)).filter(v => v > 0);
    const epMoyMoy  = epMoys.length ? q2(epMoys.reduce((a, b) => a + b, 0) / epMoys.length) : 0;
    const densites  = filtered.map(r => toF(r.Densite_Stack)).filter(v => v > 0);
    const densMoy   = densites.length ? q2(densites.reduce((a, b) => a + b, 0) / densites.length) : 0;
    const tauxCompr = filtered.map(r => toF(r.Taux_Compression)).filter(v => v > 0);
    const tauxMoy   = tauxCompr.length ? q2(tauxCompr.reduce((a, b) => a + b, 0) / tauxCompr.length) : 0;
    const alertesEp = filtered.filter(r => toF(r.Ecart_Max_Ep) > SEUIL_ECART_EP).length;
    const alertesCR = filtered.filter(r => toF(r.Taux_Compression) > SEUIL_TAUX_COMPR).length;

    // Courbe épaisseur moyenne par date
    const epParDate = {};
    filtered.forEach(r => {
      const key = (r.Date || r.Timestamp || "").split("T")[0];
      if (!epParDate[key]) epParDate[key] = { date: fmtDate(key), vals: [] };
      if (toF(r.Epaisseur_Moyenne) > 0) epParDate[key].vals.push(toF(r.Epaisseur_Moyenne));
    });
    const epCourbe = Object.values(epParDate)
      .map(d => ({ date: d.date, epaisseur: d.vals.length ? q2(d.vals.reduce((a, b) => a + b, 0) / d.vals.length) : null }))
      .filter(d => d.epaisseur !== null)
      .sort((a, b) => a.date.localeCompare(b.date));

    // Barres taux compression par stack (top 20 récents)
    const crBarres = [...filtered]
      .sort((a, b) => new Date(b.Date || b.Timestamp) - new Date(a.Date || a.Timestamp))
      .slice(0, 20)
      .map(r => ({
        stack: (r.IDStack || "").slice(-8),
        cr:    toF(r.Taux_Compression),
        color: toF(r.Taux_Compression) > SEUIL_TAUX_COMPR ? COLORS.red : COLORS.green,
      }))
      .reverse();

    // Histogramme densité (buckets de 0.1)
    const densMin = 0.5; const densMax = 2.0; const bucketSize = 0.1;
    const buckets = {};
    for (let b = densMin; b < densMax; b = q2(b + bucketSize)) {
      buckets[b.toFixed(1)] = { range: `${b.toFixed(1)}–${(b + bucketSize).toFixed(1)}`, count: 0 };
    }
    densites.forEach(d => {
      const b = (Math.floor(d / bucketSize) * bucketSize).toFixed(1);
      if (buckets[b]) buckets[b].count++;
    });
    const histoDensite = Object.values(buckets).filter(b => b.count > 0);

    // Stacked bar fraction massique (top 20)
   const fractions = [...filtered]
  .sort((a, b) => parseDate(b.Date || b.Timestamp) - parseDate(a.Date || a.Timestamp))
  .slice(0, 20)
  .map(r => {
    const bois   = toF(r.Fraction_Massique_Bois);
    const resine = toF(r.Fraction_Massique_Resine);
    return {
      stack:  (r.IDStack || "").slice(-8),
      bois:   bois   < 2 ? q2(bois   * 100) : q2(bois),
      resine: resine < 2 ? q2(resine * 100) : q2(resine),
    };
  })
  .filter(r => r.bois > 0 || r.resine > 0)
  .reverse();

    // Scatter humidité / densité plis (depuis enrichissement Empilement)
    const humDens = filtered
      .filter(r => toF(r.HumiditeMoyenne) > 0 && toF(r.DensiteMoyenne) > 0)
      .map(r => ({
        stack:    (r.IDStack || "").slice(-8),
        humidite: toF(r.HumiditeMoyenne),
        densite:  toF(r.DensiteMoyenne),
        conforme: r.Conformite === "CONFORME",
      }));

    // Table alertes épaisseur
    const alertesTable = filtered
      .filter(r => toF(r.EcartMaxEp) > SEUIL_ECART_EP)
      .sort((a, b) => toF(b.EcartMaxEp) - toF(a.EcartMaxEp))
      .slice(0, 10)
      .map(r => ({
        date:    r.Date || (r.Timestamp || "").split("T")[0],
        stack:   r.IDStack,
        ecart:   toF(r.EcartMaxEp),
        epMoy:   toF(r.Epaisseur_Moyenne),
        conf:    r.Conformite,
        op:      r.Operateur,
      }));

    return {
      conf, nonConf, epMoyMoy, densMoy, tauxMoy,
      alertesEp, alertesCR,
      epCourbe, crBarres, histoDensite, fractions, humDens, alertesTable,
    };
  }, [filtered]);

  // ── Rendu loader / erreur ──────────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...S.page, justifyContent: "center", alignItems: "center", display: "flex" }}>
      <div style={{ color: "#E6FF3E", fontSize: 16 }}>{ui("Chargement…")}</div>
    </div>
  );
  if (error) return (
    <div style={{ ...S.page, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ color: "#f87171" }}>{error}</div>
    </div>
  );

  const total = (stats?.conf || 0) + (stats?.nonConf || 0);

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div>
            <div style={S.title}>📐 Qualité Stack</div>
            <div style={S.subtitle}>Épaisseur · Compression · Densité · Fractions massiques · ILSS colle</div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input
              style={S.searchInput}
              placeholder="🔍 Filtrer par IDStack…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div style={S.periodBar}>
  {[1, 7, 30, 90, 365].map(d => (
    <button
      key={d}
      style={{ ...S.periodBtn, ...(days === d ? S.periodBtnActive : {}) }}
      onClick={() => setDays(d)}
    >
      {d === 365 ? "1an" : `${d}j`}
    </button>
  ))}
</div>
            <button style={S.backBtn} onClick={() => navigate(-1)}>← Retour</button>
          </div>
        </div>

        {/* ── KPIs ── */}
        <div style={S.kpiRow}>
          <KpiCard icon="✅" label="Conformes"
            value={stats?.conf ?? 0}
            sub={`sur ${filtered.length} stacks`}
            color={COLORS.green} />
          <KpiCard icon="❌" label="Non conformes"
            value={stats?.nonConf ?? 0}
            sub={`${total > 0 ? Math.round((stats.nonConf / total) * 100) : 0}% du total`}
            color={stats?.nonConf > 0 ? COLORS.red : COLORS.green} />
          <KpiCard icon="📏" label="Épaisseur moy."
            value={stats?.epMoyMoy ? `${stats.epMoyMoy} mm` : "—"}
            sub="moyenne des stacks"
            color={COLORS.blue} />
          <KpiCard icon="🗜️" label="Taux compression moy."
            value={stats?.tauxMoy ? `${stats.tauxMoy} %` : "—"}
            sub={`seuil : ${SEUIL_TAUX_COMPR}%`}
            color={stats?.tauxMoy > SEUIL_TAUX_COMPR ? COLORS.red : COLORS.yellow} />
          <KpiCard icon="⚖️" label="Densité moy."
            value={stats?.densMoy ? `${stats.densMoy} g/cm³` : "—"}
            sub={`[${SEUIL_DENSITE.min} – ${SEUIL_DENSITE.max}]`}
            color={COLORS.teal} />
          <KpiCard icon="🚨" label="Alertes épaisseur"
            value={stats?.alertesEp ?? 0}
            sub={`écart > ${SEUIL_ECART_EP} mm`}
            color={stats?.alertesEp > 0 ? COLORS.orange : COLORS.green} />
          <KpiCard icon="⚠️" label="Alertes compression"
            value={stats?.alertesCR ?? 0}
            sub={`CR > ${SEUIL_TAUX_COMPR}%`}
            color={stats?.alertesCR > 0 ? COLORS.red : COLORS.green} />
        </div>

        {/* ── Qualité colle ILSS (port Qualité_colle.py) ── */}
        <IlssQualiteCollePanel
          ilssItems={ilssItems}
          stacks={items}
          loading={ilssLoading}
        />

        {/* ── Ligne 2 : Conformité gauge + Courbe épaisseur ── */}
        <div style={S.row}>
          <div style={{ ...S.card, flex: 1 }}>
            <div style={S.cardTitle}>Conformité globale</div>
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 10 }}>
              <Gauge label="CONFORME" value={stats?.conf ?? 0} total={total} color={COLORS.green} />
            </div>
            <div style={S.confLegend}>
              <div style={{ ...S.confDot, background: COLORS.green }} />
              <span style={{ color: COLORS.green }}>Conforme : {stats?.conf}</span>
              <div style={{ ...S.confDot, background: COLORS.red, marginLeft: 16 }} />
              <span style={{ color: COLORS.red }}>Non conforme : {stats?.nonConf}</span>
            </div>
          </div>

          <div style={{ ...S.card, flex: 3 }}>
            <div style={S.cardTitle}>Évolution épaisseur moyenne (mm)</div>
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={stats?.epCourbe || []} margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 11 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} domain={["auto", "auto"]} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="epaisseur" name="Épaisseur moy. (mm)"
                  stroke={COLORS.blue} strokeWidth={2.5} dot={{ r: 3, fill: COLORS.blue }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Ligne 3 : Taux compression + Histogramme densité ── */}
        <div style={S.row}>
          <div style={{ ...S.card, flex: 2 }}>
            <div style={S.cardTitle}>
              Taux de compression par stack (20 derniers)
              <span style={{ ...S.badge, background: "rgba(248,113,113,0.15)", color: COLORS.red, borderColor: "rgba(248,113,113,0.3)" }}>
                seuil {SEUIL_TAUX_COMPR}%
              </span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats?.crBarres || []} margin={{ top: 8, right: 20, left: -10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                <XAxis dataKey="stack" tick={{ fill: "#64748b", fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} unit="%" />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={SEUIL_TAUX_COMPR} stroke={COLORS.red} strokeDasharray="5 3"
                  label={{ value: `Seuil ${SEUIL_TAUX_COMPR}%`, fill: COLORS.red, fontSize: 11, position: "right" }} />
                <Bar dataKey="cr" name="Taux compression (%)" radius={[4, 4, 0, 0]}>
                  {(stats?.crBarres || []).map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ ...S.card, flex: 1.5 }}>
            <div style={S.cardTitle}>
              Distribution densité (g/cm³)
              <span style={{ ...S.badge, background: "rgba(45,212,191,0.15)", color: COLORS.teal, borderColor: "rgba(45,212,191,0.3)" }}>
                {SEUIL_DENSITE.min}–{SEUIL_DENSITE.max} g/cm³
              </span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats?.histoDensite || []} margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                <XAxis dataKey="range" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine x={`${SEUIL_DENSITE.min.toFixed(1)}–${(SEUIL_DENSITE.min + 0.1).toFixed(1)}`}
                  stroke={COLORS.orange} strokeDasharray="4 2" />
                <Bar dataKey="count" name="Nb stacks" radius={[4, 4, 0, 0]}>
                  {(stats?.histoDensite || []).map((entry, i) => {
                    const mid = parseFloat(entry.range);
                    const ok  = mid >= SEUIL_DENSITE.min && mid <= SEUIL_DENSITE.max;
                    return <Cell key={i} fill={ok ? COLORS.teal : COLORS.orange} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Ligne 4 : Fractions massiques + Scatter humidité/densité ── */}
        <div style={S.row}>
          <div style={{ ...S.card, flex: 2 }}>
            <div style={S.cardTitle}>Fractions massiques bois / résine (20 derniers)</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats?.fractions || []} margin={{ top: 8, right: 20, left: -10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                <XAxis dataKey="stack" tick={{ fill: "#64748b", fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} unit="%" />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8" }} />
                <Bar dataKey="bois"   name="Bois (%)"   fill={COLORS.orange} radius={[4, 4, 0, 0]} stackId="a" />
                <Bar dataKey="resine" name="Résine (%)" fill={COLORS.blue}   radius={[4, 4, 0, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ ...S.card, flex: 1.5 }}>
            <div style={S.cardTitle}>
              Humidité vs Densité plis
              <span style={{ ...S.badge, background: "rgba(100,116,139,0.15)", color: "#94a3b8", borderColor: "rgba(100,116,139,0.3)" }}>
                via Empilement
              </span>
            </div>
            {(stats?.humDens?.length || 0) === 0 ? (
              <div style={S.empty}>Données humidité/densité non disponibles</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <ScatterChart margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                  <XAxis dataKey="humidite" name="Humidité (%)" tick={{ fill: "#64748b", fontSize: 11 }} unit="%" />
                  <YAxis dataKey="densite"  name="Densité"      tick={{ fill: "#64748b", fontSize: 11 }} unit=" g/cm³" />
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div style={S.tooltip}>
                          <div style={{ color: "#E6FF3E", fontWeight: 700, fontSize: 12 }}>{d?.stack}</div>
                          <div style={{ color: COLORS.blue,   fontSize: 12 }}>Humidité : {d?.humidite} %</div>
                          <div style={{ color: COLORS.orange, fontSize: 12 }}>Densité  : {d?.densite} g/cm³</div>
                          <div style={{ color: d?.conforme ? COLORS.green : COLORS.red, fontSize: 12 }}>
                            {d?.conforme ? "✅ Conforme" : "❌ Non conforme"}
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Scatter
                    data={stats.humDens}
                    fill={COLORS.teal}
                    shape={(props) => {
                      const { cx, cy, payload } = props;
                      return <circle cx={cx} cy={cy} r={5}
                        fill={payload.conforme ? COLORS.green : COLORS.red}
                        fillOpacity={0.8} stroke="#fff" strokeWidth={0.5} />;
                    }}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ── Ligne 5 : Table alertes épaisseur ── */}
        {(stats?.alertesTable?.length || 0) > 0 && (
          <div style={S.card}>
            <div style={S.cardTitle}>
              🚨 Alertes écart max épaisseur
              <span style={{ ...S.badge, background: "rgba(251,146,60,0.15)", color: COLORS.orange, borderColor: "rgba(251,146,60,0.3)" }}>
                écart &gt; {SEUIL_ECART_EP} mm
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {["Date", "ID Stack", "Écart max (mm)", "Ép. moy. (mm)", "Conformité", "Opérateur"].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stats.alertesTable.map((r, i) => (
                    <tr key={i} style={i % 2 === 0 ? S.trEven : S.trOdd}>
                      <td style={S.td}>{r.date}</td>
                      <td style={{ ...S.td, color: "#E6FF3E", fontWeight: 700, fontFamily: "monospace" }}>{r.stack}</td>
                      <td style={{ ...S.td, color: COLORS.orange, fontWeight: 700 }}>{r.ecart}</td>
                      <td style={S.td}>{r.epMoy}</td>
                      <td style={S.td}>
                        <span style={{
                          ...S.badge,
                          background: r.conf === "CONFORME" ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.12)",
                          color:      r.conf === "CONFORME" ? COLORS.green : COLORS.red,
                          borderColor: r.conf === "CONFORME" ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)",
                        }}>
                          {r.conf}
                        </span>
                      </td>
                      <td style={{ ...S.td, color: "#94a3b8" }}>{r.op}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:          { minHeight: "100vh", background: "linear-gradient(160deg,#041e2e 0%,#063549 60%,#052a3e 100%)", color: "#e2e8f0", fontFamily: "'Inter',sans-serif", padding: "24px 28px" },
  wrap:          { maxWidth: 1600, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 },
  header:        { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 },
  title:         { fontSize: 26, fontWeight: 900, background: "linear-gradient(90deg,#fff,#E6FF3E)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" },
  subtitle:      { fontSize: 13, color: "#94a3b8", marginTop: 4 },
  backBtn:       { padding: "9px 18px", borderRadius: 8, border: "1px solid #1e3a50", background: "rgba(255,255,255,0.04)", color: "#cbd5e1", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  searchInput:   { padding: "8px 14px", borderRadius: 8, border: "1px solid #1e3a50", background: "rgba(255,255,255,0.04)", color: "#e2e8f0", fontSize: 13, outline: "none", width: 200 },
  periodBar:     { display: "flex", gap: 4, background: "rgba(255,255,255,0.04)", border: "1px solid #1e3a50", borderRadius: 8, padding: 4 },
  periodBtn:     { padding: "6px 14px", borderRadius: 6, border: "none", background: "transparent", color: "#94a3b8", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  periodBtnActive: { background: "#E6FF3E", color: "#000" },
  kpiRow:        { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14 },
  kpiCard:       { background: "rgba(255,255,255,0.03)", border: "1px solid #1e3a50", borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, borderTopWidth: 3, backdropFilter: "blur(8px)" },
  kpiIcon:       { fontSize: 22 },
  kpiValue:      { fontSize: 30, fontWeight: 900 },
  kpiLabel:      { fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "center" },
  kpiSub:        { fontSize: 11, color: "#64748b" },
  row:           { display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 },
  card:          { background: "rgba(255,255,255,0.03)", border: "1px solid #1e3a50", borderRadius: 14, padding: "20px 22px", backdropFilter: "blur(8px)" },
  cardTitle:     { fontSize: 13, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  badge:         { display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontWeight: 700, fontSize: 11, border: "1px solid", textTransform: "none", letterSpacing: 0 },
  gaugeWrap:     { display: "flex", flexDirection: "column", alignItems: "center", gap: 6 },
  gaugeLabel:    { fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" },
  confLegend:    { display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginTop: 12, fontSize: 13, flexWrap: "wrap" },
  confDot:       { width: 10, height: 10, borderRadius: "50%" },
  tooltip:       { background: "#071e2f", border: "1px solid #1e3a50", borderRadius: 8, padding: "10px 14px" },
  empty:         { textAlign: "center", padding: "40px 20px", color: "#64748b", fontSize: 13 },
  table:         { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:            { padding: "11px 14px", background: "#071e2f", color: "#94a3b8", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left", borderBottom: "1px solid #1e3a50", whiteSpace: "nowrap" },
  td:            { padding: "11px 14px", borderBottom: "1px solid #0f2a3d", color: "#cbd5e1", whiteSpace: "nowrap" },
  trEven:        { background: "transparent" },
  trOdd:         { background: "rgba(255,255,255,0.015)" },
};